"use client"

import { useId, useMemo } from "react"
import {
  PLAQUE_ICON_PATHS,
  PLAQUE_SPEC,
  layoutPlaque,
  plaqueBorderPath,
  plaqueOutlinePath,
  type PlaqueDesign,
  type PlaqueMeasure,
} from "@/lib/plaqueDesign"
import { PLAQUE_BROWSER_FONTS } from "./plaqueFonts"

const { widthMm: W, heightMm: H } = PLAQUE_SPEC
// Room around the plate for the drop shadow.
const PAD = 5
const OUTLINE = plaqueOutlinePath()
const BORDER = plaqueBorderPath()

/**
 * Brushed-gold mockup drawn at true proportions (1 SVG unit = 1 mm). Uses the
 * same layoutPlaque() and outline as the production file, so what the owner
 * sees is what gets engraved.
 */
export default function PlaquePreview({
  design,
  measure,
  placeholder = false,
  className,
}: {
  design: PlaqueDesign
  measure?: PlaqueMeasure
  /** Render the text faded — used while the name is still empty. */
  placeholder?: boolean
  className?: string
}) {
  const uid = useId().replace(/:/g, "")
  const layout = useMemo(() => layoutPlaque(design, measure), [design, measure])
  const ink = "#1d1812"

  return (
    <svg
      viewBox={`${-PAD} ${-PAD} ${W + 2 * PAD} ${H + 2 * PAD}`}
      className={className}
      role="img"
      aria-label={`Plaque preview: ${layout.lines.map((l) => l.text).join(", ")}`}
    >
      <defs>
        <linearGradient id={`gold-${uid}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#b8924f" />
          <stop offset="0.3" stopColor="#e2c785" />
          <stop offset="0.55" stopColor="#c9a65f" />
          <stop offset="0.8" stopColor="#e6cf93" />
          <stop offset="1" stopColor="#a8823f" />
        </linearGradient>
        <linearGradient id={`sheen-${uid}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0.32" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
        {/* Horizontal brushing: noise stretched along x, turned into faint light/dark streaks. */}
        <filter id={`brush-${uid}`} x="0" y="0" width="100%" height="100%">
          <feTurbulence type="fractalNoise" baseFrequency="0.015 1.6" numOctaves="2" seed="7" />
          <feColorMatrix type="matrix" values="0 0 0 0 0.35  0 0 0 0 0.27  0 0 0 0 0.12  0 0 0 0.9 -0.32" />
        </filter>
        <filter id={`shadow-${uid}`} x="-10%" y="-15%" width="120%" height="140%">
          <feDropShadow dx="0.3" dy="1.2" stdDeviation="1.3" floodColor="#1a0d06" floodOpacity="0.55" />
        </filter>
        <clipPath id={`clip-${uid}`}>
          <path d={OUTLINE} />
        </clipPath>
      </defs>

      {/* Plate */}
      <path d={OUTLINE} fill={`url(#gold-${uid})`} filter={`url(#shadow-${uid})`} />
      <g clipPath={`url(#clip-${uid})`}>
        <rect width={W} height={H} filter={`url(#brush-${uid})`} />
        <polygon points={`${W * 0.12},0 ${W * 0.3},0 ${W * 0.14},${H} ${W * -0.04},${H}`} fill={`url(#sheen-${uid})`} />
      </g>
      <path d={OUTLINE} fill="none" stroke="#6e5226" strokeOpacity="0.8" strokeWidth="0.25" />

      {/* Engraving, paint-filled dark. A faint light offset underneath reads as cut into the surface. */}
      <g>
        {[
          { dy: 0.16, fill: "#fbefc8", opacity: 0.55 },
          { dy: 0, fill: ink, opacity: 1 },
        ].map((pass) => (
          <g key={pass.fill} fill={pass.fill} opacity={pass.opacity} transform={`translate(0 ${pass.dy})`}>
            <path d={BORDER} fillRule="evenodd" />
            <g opacity={placeholder ? 0.35 : 1}>
            {layout.rules.map((r, i) => (
              <rect key={`r${i}`} x={r.x} y={r.y} width={r.width} height={r.height} />
            ))}
            {layout.icons.map((icon, i) => (
              <path
                key={`i${i}`}
                d={PLAQUE_ICON_PATHS[icon.icon]}
                transform={`translate(${icon.x} ${icon.y}) scale(${icon.sizeMm / 24})`}
              />
            ))}
            {layout.lines.map((line, i) => {
              const f = PLAQUE_BROWSER_FONTS[line.face]
              return (
                <text
                  key={`${line.role}${i}`}
                  x={line.x}
                  y={line.baseline}
                  fontSize={line.sizeMm}
                  letterSpacing={line.trackingMm}
                  style={{ fontFamily: f.fontFamily, fontWeight: f.fontWeight, fontStyle: f.fontStyle }}
                  xmlSpace="preserve"
                >
                  {line.text}
                </text>
              )
            })}
            </g>
          </g>
        ))}
      </g>
    </svg>
  )
}
